import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { allTypesExhausted } from './card-email';
import {
  CARD_LIMIT,
  advanceStatus,
  isActionable,
  type Card,
  type CardOperation,
  type CardOperationStatus,
  type CardStatistics,
  type CardTransaction,
  type CardType,
} from './card.types';

interface CardState {
  cards: Card[];
  /** `GET /cards/available-types` — shared by the list page and the issue wizard. */
  types: CardType[];
  typesLoaded: boolean;
  operations: CardOperation[];
  transactions: CardTransaction[];
  transactionsCount: number;
  statistics: CardStatistics | null;
  activeOperationId: string | null;
  refunds: Record<string, { amountRaw: bigint; txHash: string | null }>;
  loading: boolean;
  errorKey: string | null;
  degraded: boolean;
}

const initialState: CardState = {
  cards: [],
  types: [],
  typesLoaded: false,
  operations: [],
  transactions: [],
  transactionsCount: 0,
  statistics: null,
  activeOperationId: null,
  refunds: {},
  loading: true,
  errorKey: null,
  degraded: false,
};

export const CardStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(store => ({
    /** True once the backend reported `remainingForUser` on at least one type (CARD-002). */
    limitFromServer: computed(() => store.types().some(t => t.remainingForUser !== null)),
    /**
     * Server-driven when `remainingForUser` is known (every type at zero ⇒ account limit),
     * otherwise the legacy client-side `CARD_LIMIT` fallback.
     */
    atLimit: computed(() => {
      const types = store.types();
      if (types.some(t => t.remainingForUser !== null)) return allTypesExhausted(types);
      return store.cards().filter(isActionable).length >= CARD_LIMIT;
    }),
    activeOperation: computed(
      () => store.operations().find(op => op.id === store.activeOperationId()) ?? null,
    ),
  })),
  withMethods(store => {
    const resetHooks: (() => void)[] = [];
    return {
      registerResetHook(hook: () => void): void {
        resetHooks.push(hook);
      },
      setCards(cards: Card[]): void {
        patchState(store, { cards, loading: false });
      },
      setTypes(types: CardType[]): void {
        patchState(store, { types, typesLoaded: true });
      },
      /** Forget the catalogue so the next screen re-reads counters (e.g. right after an issue). */
      invalidateTypes(): void {
        patchState(store, { typesLoaded: false });
      },
      setLoading(loading: boolean): void {
        patchState(store, { loading });
      },
      setError(errorKey: string, degraded: boolean): void {
        patchState(store, { errorKey, degraded, loading: false });
      },
      clearError(): void {
        patchState(store, { errorKey: null, degraded: false });
      },
      upsertCard(card: Card): void {
        const exists = store.cards().some(c => c.id === card.id);
        patchState(store, {
          cards: exists
            ? store.cards().map(c => (c.id === card.id ? card : c))
            : [...store.cards(), card],
        });
      },
      setOperations(operations: CardOperation[]): void {
        patchState(store, { operations });
      },
      mergeOperations(operations: CardOperation[]): void {
        const current = new Map(store.operations().map(op => [op.id, op]));
        const merged = operations.map(op => {
          const known = current.get(op.id);
          return known ? { ...op, status: advanceStatus(known.status, op.status) } : op;
        });
        const incoming = new Set(merged.map(op => op.id));
        const kept = store.operations().filter(op => !incoming.has(op.id));
        patchState(store, { operations: [...merged, ...kept] });
      },
      setTransactions(transactions: CardTransaction[], count = transactions.length): void {
        patchState(store, { transactions, transactionsCount: count });
      },
      setStatistics(statistics: CardStatistics): void {
        patchState(store, { statistics });
      },
      patchOperationStatus(operationId: string, status: CardOperationStatus): void {
        patchState(store, {
          operations: store
            .operations()
            .map(op =>
              op.id === operationId ? { ...op, status: advanceStatus(op.status, status) } : op,
            ),
        });
      },
      patchOperationFailure(operationId: string, failureReason: string | null): void {
        patchState(store, {
          operations: store
            .operations()
            .map(op => (op.id === operationId ? { ...op, failureReason } : op)),
        });
      },
      addOperation(operation: CardOperation): void {
        const known = store.operations().some(op => op.id === operation.id);
        patchState(store, {
          operations: known
            ? store.operations().map(op => (op.id === operation.id ? operation : op))
            : [operation, ...store.operations()],
          activeOperationId: operation.id,
        });
      },
      setRefund(operationId: string, amountRaw: bigint, txHash: string | null): void {
        patchState(store, {
          refunds: { ...store.refunds(), [operationId]: { amountRaw, txHash } },
        });
      },
      setActiveOperation(id: string | null): void {
        patchState(store, { activeOperationId: id });
      },
      reset(): void {
        for (const hook of resetHooks) hook();
        patchState(store, initialState);
      },
    };
  }),
);
